import type { Messages } from './types'

export const en: Messages = {
  meta: {
    language: 'English',
    htmlLang: 'en',
  },

  site: {
    name: 'Mazzella Rent',
    tagline: 'On Ischia since 1965',
    skipToContent: 'Skip to content',
    languageSwitcher: 'Site language',
    sampleBanner:
      'Test data: vehicles, prices and availability are samples. Bookings made here are not real.',
    footerAddress: 'Ferry Terminal and Hydrofoil Terminal, Ischia Porto',
    bookingTitle: 'Book a car, scooter or e-bike on Ischia',
    bookingDescription:
      'Car, scooter and e-bike rental on Ischia: live prices and availability, pick-up at the port or delivery to your hotel.',
  },

  common: {
    back: 'Back',
    continue: 'Continue',
    edit: 'Change',
    retry: 'Try again',
    loading: 'Loading…',
    optional: 'optional',
    total: 'Total',
    select: 'Select',
    yes: 'Yes',
    no: 'No',
    days: { one: '{count} day', other: '{count} days' },
    perDay: '{amount} a day',
    perRental: '{amount} per rental',
    steps: 'Step {current} of {total}',
    stepNames: { search: 'Search', results: 'Vehicle', options: 'Extras', details: 'Details' },
    vehicle: 'Vehicle',
  },

  legs: {
    pickup: 'Pick-up',
    return: 'Return',
  },

  search: {
    title: 'Book your vehicle',
    intro:
      'Cars, scooters and e-bikes on Ischia. Tell us where and when, and we show you prices and availability straight away.',
    pickupPlace: 'Pick-up place',
    returnPlace: 'Return place',
    sameAsPickup: 'Same as pick-up',
    officesGroup: 'Our offices',
    deliveryGroup: 'Delivery',
    deliveryFee: '{name} (delivery {amount})',
    pickupDate: 'Pick-up date',
    pickupTime: 'Pick-up time',
    returnDate: 'Return date',
    returnTime: 'Return time',
    rentalDay: 'A rental day runs from {time} to {time} the next day.',
    closed: 'Closed on this date',
    noTimesLeft: 'No times left today',
    promoToggle: 'Have a promo code?',
    promoCode: 'Promo code',
    submit: 'Check availability',
    returnBeforePickup: 'The return must be after the pick-up.',
  },

  results: {
    title: 'Choose your vehicle',
    period: '{from} → {to}',
    choose: 'Choose',
    unavailable: 'Sold out for these dates',
    notBookable: 'Not bookable for these dates',
    none: 'No vehicles are available for these dates. Try other dates or another place.',
    editSearch: 'Change search',
    promoApplied: 'Code {code} applied.',
    promoNotApplied: 'Code {code} not applied: {reason}',
    promoBlocks:
      'This code cannot be used for this search: change the dates or the place, or search without the code.',
    searchWithoutCode: 'Search without the code',
  },

  options: {
    title: 'Extras and cover',
    extras: 'Extras',
    noExtras: 'No extras are available for this vehicle.',
    quantity: 'Quantity of {name}',
    coverages: 'Cover',
    included: 'Included',
    excess: 'Excess {amount}',
    summary: 'Your rental',
    updating: 'Updating the price…',
  },

  details: {
    title: 'Your details',
    driver: 'Driver',
    driverNote: 'Only the person who signs the rental contract may drive.',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    phoneHint: 'With the international prefix, for example +44 7700 900123.',
    birthDate: 'Date of birth',
    licence: 'Driving licence',
    licenceAccepted: 'Licences accepted for this vehicle: {categories}.',
    licenceYears: {
      one: 'You must have held the category for at least {count} year.',
      other: 'You must have held the category for at least {count} years.',
    },
    licenceNumber: 'Licence number',
    issuingCountry: 'Issuing country',
    licenceCategory: 'Category',
    firstIssuedOn: 'Date you obtained the category',
    firstIssuedHint: 'It is the date in field 10 of an EU licence.',
    expiresOn: 'Expiry date',
    hasIdp: 'I also have an International Driving Permit (IDP)',
    notes: 'Notes for the office',
    termsBefore: 'I have read and accept the',
    termsLink: 'rental terms and conditions',
    termsVersion: 'Version {version}',
    privacyBefore: 'How we use your data:',
    privacyLink: 'privacy notice',
    paymentTitle: 'Pay at pick-up',
    paymentReadyTitle: 'Payment',
    paymentReady:
      'Once confirmed, you can pay online straight away on a secure Stripe page, or at pick-up in cash or by card.',
    paymentPending:
      'Online payment is not available yet: you pay at the office when you pick up, in cash or by card.',
    deposit: 'A security deposit on a credit or debit card is required at pick-up.',
    submit: 'Confirm booking',
    submitting: 'Sending…',
    pendingNotice:
      'We do not know whether your last request arrived. We check it first, so no second booking is made.',
    checkPending: 'Check the earlier request',
    priceChanged:
      'The price has changed: the new total is {amount}. Please check it and confirm again.',
  },

  done: {
    title: 'Booking confirmed',
    titleEnded: 'Your booking',
    code: 'Booking code',
    codeHint: 'Keep this code: you will need it at pick-up.',
    status: 'Status',
    payAtCounter: 'You pay {amount} at pick-up, in cash or by card.',
    counterOnly: 'You pay {amount} at our counter, in cash or by card.',
    checks: 'At pick-up we will check:',
    documents: 'Documents',
    documentsIntro: 'Optional: upload your ID and driving licence now to make pick-up quicker.',
    documentKind: 'Document',
    documentSide: 'Side',
    file: 'Choose the file',
    noFile: 'No file chosen',
    fileHint: 'JPG, PNG or PDF, up to {size}.',
    upload: 'Upload',
    uploading: 'Uploading…',
    uploaded: '{document}: uploaded',
    newBooking: 'New booking',
    restoreFailed: 'We cannot reload your booking right now.',
    payTitle: 'Payment',
    payIntro: 'You can pay online now, or at pick-up in cash or by card.',
    payOnline: 'Pay {amount} now',
    payRedirecting: 'Taking you to the payment page…',
    payHint: 'You pay on a secure Stripe page, then come back here.',
    paySample:
      'A test payment: nothing is charged. Use card 4242 4242 4242 4242, any future date and any CVC.',
    ended:
      'This booking is no longer active: nothing is paid online and no documents are needed. For questions about payments or refunds, contact us.',
    paidTitle: 'Payment received',
    paidAmount: 'Paid: {amount}.',
    balanceDue: 'Still to pay: {amount}.',
    paymentConfirming: 'Confirming your payment…',
    paymentSlow:
      'The confirmation is taking longer than usual. If you paid, it will show here shortly: please do not pay twice.',
    paymentProcessing:
      'Your bank is still confirming the payment. There is nothing else to do: it will show here.',
    paymentCancelled: 'The payment was not completed. You can try again or pay at pick-up.',
    paymentFailed: 'The payment did not go through. You can try again or pay at pick-up.',
    paymentNoBooking:
      'You are back from the payment page, but this window no longer has your booking. If you paid, the payment is recorded and the office will see it at pick-up.',
    payLinkInvalid:
      'This payment link has expired or is not valid. Ask Mazzella Rent for a new one, or pay at pick-up.',
  },

  validation: {
    required: 'This field is required.',
    email: 'Please check the email address.',
    phone: 'Enter the number with the international prefix, for example +44 7700 900123.',
    date: 'Choose a valid date.',
    pastDate: 'The date must be in the past.',
    terms: 'To book, you need to accept the rental terms and conditions.',
    fileType: 'Unsupported format: use JPG, PNG or PDF.',
    fileSize: 'The file is too large: up to {size}.',
  },

  vehicleKinds: {
    car: 'Car',
    van: 'Van',
    minivan: 'Minivan',
    scooter: 'Scooter',
    motorcycle: 'Motorbike',
    ebike: 'E-bike',
  },

  quoteLines: {
    rental: 'Rental',
    surcharge: 'Surcharge',
    discount: 'Discount {code}',
    delivery: 'Delivery: {name}',
    collection: 'Collection at the end: {name}',
    one_way: 'Return to a different place',
    extra: '{name}',
    coverage: '{name}',
  },

  youngDriver: 'Young driver surcharge',

  refusals: {
    invalid_period: 'The return must be after the pick-up.',
    in_the_past: 'That pick-up time has passed: choose a future date.',
    category_unavailable: 'This vehicle cannot be booked online.',
    season_not_configured: 'We do not have prices yet from {from} to {to}.',
    rate_not_configured: 'We do not have a price yet for this length of rental.',
    day_price_not_configured: 'We do not have prices yet from {from} to {to}.',
    stop_sale: 'We are not taking online bookings from {from} to {to}: call or write to us.',
    min_stay: 'For these dates the minimum rental is {minDays} days.',
    place_unknown: 'This place is no longer available.',
    outside_opening_hours: '{leg} at {time}: the {location} office is closed at that time.',
    delivery_blackout: '{leg} · {zone}: not available during this period.',
    delivery_lead_time: '{leg} · {zone}: we need at least {minutes} minutes’ notice.',
    delivery_cutoff: '{leg} · {zone}: book it by {cutoff} the day before.',
    delivery_fee_not_configured: '{leg} · {zone}: not bookable online.',
    one_way_fee_not_configured: 'Returning to a different place is not available online.',
    extra_unknown: 'An extra you chose is no longer available.',
    extra_not_for_category: '{extra} is not available for this vehicle.',
    extra_quantity: '{extra}: at most {max} per booking.',
    extra_out_of_stock: '{extra}: only {left} left for these dates.',
    coverage_unknown: 'A cover you chose is no longer available.',
    driver_too_young: 'For this vehicle the driver must be at least {minAge}.',
    young_driver_surcharge_not_configured:
      'For a driver of your age this vehicle can only be booked at the office.',
  },

  promoRefusals: {
    unknown: 'the code does not exist.',
    inactive: 'the code is not active.',
    not_yet_valid: 'the code is not valid yet.',
    expired: 'the code has expired.',
    exhausted: 'the code has been used up.',
    blackout: 'the code is not valid on these dates.',
    location: 'the code is only valid for some pick-up and return places.',
  },

  eligibility: {
    rules_not_configured: 'the driver requirements for this vehicle.',
    birth_date_missing: 'your date of birth.',
    too_young: 'the minimum age: you must be at least {minAge}.',
    licence_rules_not_configured: 'the licence needed for this vehicle.',
    licence_missing: 'your driving licence.',
    licence_expired: 'the licence expiry date ({expiresOn}).',
    licence_category: 'the licence category: we accept {accepted}.',
    licence_first_issue_unknown: 'how long you have held your licence.',
    licence_too_recent: 'how long you have held your licence: at least {minYears} years.',
    idp_required:
      'an International Driving Permit, required with a licence from this country: {country}.',
    manual_check: 'your licence, as we do for every rental of this vehicle.',
  },

  documentKinds: {
    id_card: 'ID card',
    passport: 'Passport',
    driving_licence: 'Driving licence',
    other: 'Other document',
  },

  documentSides: {
    front: 'Front',
    back: 'Back',
    single: 'Single page',
  },

  reservationStatuses: {
    draft: 'Pending',
    confirmed: 'Confirmed',
    checked_out: 'In progress',
    returned: 'Returned',
    closed: 'Closed',
    cancelled: 'Cancelled',
    no_show: 'No-show',
  },

  payment: {
    /** The line Stripe's page shows. */
    item: 'Mazzella Rent rental, booking {code}',
    itemSample: 'Test payment: booking {code}',
  },

  emails: {
    greeting: 'Hello {name},',
    signature: 'Mazzella Rent, Ischia since 1965',
    sample: 'Test booking: no vehicle is actually reserved.',
    automatic: 'This e-mail is sent automatically: please do not reply to this address.',
    call: 'To change anything, call us on {phone}.',
    pickupReminder: {
      subject: 'Reminder: pick-up {when} · booking {code}',
      intro: 'a reminder of your pick-up.',
      ferry: 'Ferry',
    },
    returnReminder: {
      subject: 'Reminder: return {when} · rental {code}',
      intro: 'a reminder that the vehicle is due back.',
      due: 'There is {amount} still to pay: you pay it on return, in cash or by card.',
    },
    questions: 'For any question, call us on {phone}.',
    /** Q-97 (S7): a cancellation, facts only: no clause is quoted. */
    bookingCancelled: {
      subject: 'Booking {code} cancelled',
      intro: 'your booking has been cancelled.',
      paid: 'Paid',
      fee: 'Cancellation fee',
      returned: 'Already refunded',
      toReturn: 'To be refunded',
      refundFollows: 'We will e-mail you again when the refund has been sent.',
    },
    /** W2-S S4: a website booking's hold let go, its online share not paid in time. */
    holdReleased: {
      subject: 'Booking {code} not completed',
      intro:
        'the online payment for your booking was not completed in time, so the vehicle is no longer held for you and nothing has been charged.',
      bookAgain: 'You can book again here: {url}',
    },
    /** Q-97 (S7): a refund sent; "sent", never "received": a card refund can still fail. */
    refundSent: {
      subject: 'Booking {code}: refund of {amount}',
      intro: 'we have sent you a refund.',
      amount: 'Amount',
      method: 'How',
      methods: {
        stripe: 'To the card used for the payment',
        card_terminal: 'To your card, on our terminal',
        bank_transfer: 'By bank transfer',
        cash: 'In cash, at the counter',
      },
      timing: {
        stripe: 'It usually appears on your statement within 5–10 business days.',
        card_terminal: 'When it appears depends on your card’s bank.',
        bank_transfer: 'When it arrives depends on the banks.',
        cash: 'It was handed to you at the counter.',
      },
    },
    paymentLink: {
      subject: 'Booking {code}: your payment link',
      intro: 'here is the link to pay for your booking online.',
      introFineFee:
        'here is the link to pay online what is left for your rental: handling a traffic fine received for the rental.',
      balance: 'To pay',
      pay: "Pay securely by card on Stripe's page: {url}",
      expires: 'The link is valid until {date}.',
      ignore:
        'If you have already paid, or did not make this booking, ignore this e-mail: the link shows only the amount to pay.',
    },
    contractSigned: {
      subject: 'Rental {code}: your signed contract',
      intro: 'attached is the rental contract you signed at the counter.',
      language: 'The contract is in Italian.',
      keep: 'Please keep it for as long as the rental lasts.',
    },
    bookingConfirmed: {
      subject: 'Booking {code} confirmed',
      intro: 'your booking is confirmed.',
      code: 'Booking code',
      keepCode: 'Keep this code: you will need it at pick-up.',
      pickup: 'Pick-up',
      return: 'Return',
      vehicle: 'Vehicle',
      total: 'Total',
      payAtCounter: 'You pay {amount} at pick-up, in cash or by card.',
      payUnlessPaidOnline:
        'Unless you have already paid online, you pay {amount} at pick-up, in cash or by card.',
      partlyPaid:
        'Paid so far: {paid}. You pay the rest, {balance}, at pick-up, in cash or by card.',
      paid: 'Payment received: {amount}. Thank you!',
      bringLicence: 'Please bring an identity document and your driving licence to pick-up.',
      bringId: 'Please bring an identity document to pick-up.',
    },
    rentalReturned: {
      subject: 'Rental {code}: summary',
      intro: 'thank you for renting with us. Here is your rental summary.',
      code: 'Booking code',
      vehicle: 'Vehicle',
      returned: 'Returned',
      km: 'Kilometres driven',
      rental: 'Rental',
      paid: 'Paid',
      balance: 'Still to pay',
      settled: 'Everything is paid. Thank you, and see you soon!',
      due: '{amount} is still to pay.',
      notReceipt: 'This summary is not a tax document.',
      chargeKinds: {
        fuel: 'Fuel',
        late: 'Late return',
        cleaning: 'Cleaning',
        damage: 'Damage',
        other: 'Other',
        fine_fee: 'Handling a traffic fine received for the rental',
        excess_km: 'Excess kilometres',
      },
    },
  },

  signing: {
    title: 'Your rental contract',
    intro: 'Read the contract and sign in the two boxes at the bottom.',
    language: 'The contract and its terms are in Italian. If anything is unclear, ask the staff.',
    draft: 'DRAFT: test terms, with no legal value.',
    signMain: 'Sign here to accept the contract and its terms',
    signApproval: 'Sign here to specifically approve the points listed above',
    pad: 'Signature box: sign with your finger or the pen.',
    padEmpty: 'No signature',
    padSigned: 'Signed',
    clear: 'Clear the signature',
    submit: 'Sign the contract',
    submitHint: 'Sign in both boxes to continue.',
    signedTitle: 'Thank you, the contract is signed',
    signedBody: 'Please hand the device back to the staff.',
    signedEmail: 'You will receive a copy of the contract by e-mail.',
    done: 'Hand the device back',
    cancel: 'Not signing now',
    expires: 'This screen closes at {time}.',
    /** C7: the contract could not be read; said in it, en and de, the booking's language unknown. */
    unavailable: 'The contract will not open on this device. Please hand it back to the staff.',
  },

  contract: {
    title: 'Rental contract',
    booking: 'Booking {code}',
    draft: 'DRAFT',
    draftNotice: 'Draft: test terms, with no legal value.',
    sections: {
      company: 'Lessor',
      customer: 'Customer',
      licence: 'Driving licence',
      secondDriver: 'Second driver',
      vehicle: 'Vehicle',
      period: 'Rental period',
      prices: 'Prices',
      cover: 'Cover',
      deposit: 'Security deposit',
      fees: 'Penalties and fees',
      approval: 'Specific approval (Italian Civil Code, art. 1341 and 1342)',
      signatures: 'Signatures',
    },
    fields: {
      name: 'Name',
      address: 'Address',
      vatNumber: 'VAT number',
      birthDate: 'Date of birth',
      birthPlace: 'Place of birth',
      nationality: 'Nationality',
      email: 'E-mail',
      phone: 'Phone',
      licenceNumber: 'Number',
      issuingCountry: 'Issued in',
      expiresOn: 'Expires',
      categories: 'Categories',
      plate: 'Plate',
      model: 'Model',
      category: 'Category',
      from: 'Pick-up',
      to: 'Return',
      total: 'Total',
      excess: 'Excess',
    },
    notConfigured: 'not configured yet',
    noLicence: 'No licence on file',
    noCover: 'Third-party liability only (RCA)',
    noExcess: 'none',
    depositPending: 'To be decided: the amount and the method are not set yet.',
    depositAmount: '{amount}, method to be decided',
    adjustment: 'Change agreed at the counter',
    secondDriverFee: 'Second driver',
    fees: {
      fuel_refill: 'Fuel not refilled',
      one_way: 'Return at another place',
      fine_admin: 'Handling a traffic fine received for the rental',
    },
    signatureMain: 'Customer’s signature',
    signatureApproval: 'Signature for specific approval',
    signedAt: 'Signed on {date}',
    fingerprint: 'SHA-256 fingerprint of the contract: {hash}',
    termsFingerprint: 'Terms {version}, SHA-256 fingerprint: {hash}',
  },

  errors: {
    network: 'We cannot reach the service. Check your connection and try again.',
    fallback: 'Something went wrong. Please try again shortly.',
    codes: {
      validation_failed: 'Some details are not valid. Check the fields and try again.',
      bad_request: 'The request is not valid. Reload the page and try again.',
      not_found: 'We could not find what you are looking for.',
      internal_error: 'Something went wrong on our side. Please try again shortly.',
      rate_limited: 'Too many requests in a short time. Try again in {retryAfterSeconds} seconds.',
      app_version_required: 'Reload the page and try again.',
      app_version_unsupported: 'A new version is available. Please reload the page.',
      not_configured: 'Online booking is not set up for these dates yet.',
      category_not_found: 'This vehicle is no longer available.',
      category_inactive: 'This vehicle is no longer available.',
      not_available: 'Sorry: the vehicle is no longer available for these dates.',
      quote_refused: 'We cannot price this choice.',
      promo_refused: 'The promo code is not valid for this booking.',
      not_eligible: 'The driver does not meet the requirements for this vehicle.',
      price_changed: 'The price has changed. Check the new total and confirm again.',
      terms_changed: 'The rental terms have changed. Read them and accept them again.',
      terms_draft:
        'Signing and booking are not possible right now: the rental terms are not available. Please ask us.',
      idempotency_key_required: 'Reload the page and try again.',
      idempotency_key_invalid: 'Reload the page and try again.',
      idempotency_key_reused:
        'This request was already sent with different details. Reload the page.',
      idempotency_key_expired:
        'This request is too old to be repeated. Reload the page and check before trying again.',
      booking_not_found: 'No booking matches these details.',
      booking_token_invalid: 'This link has expired. Look up your booking again.',
      upload_link_invalid: 'The upload link has expired. Try again.',
      already_uploaded: 'This file has already been uploaded.',
      booking_closed: 'This booking is no longer active: no documents can be added.',
      file_too_large: 'The file is too large.',
      unsupported_content_type: 'Unsupported format: use JPG, PNG or PDF.',
      wrong_content_type: 'The file does not match the format given. Try again.',
      content_mismatch: 'The file’s content does not match its format.',
      too_many_documents: 'You have already uploaded the maximum number of documents.',
      vehicle_not_in_category: 'This vehicle is no longer available.',
      hold_required: 'Something went wrong on our side. Please try again shortly.',
      illegal_transition: 'This booking can no longer be changed.',
      version_conflict: 'The details changed in the meantime. Reload the page.',
      invalid_period: 'The return must be after the pick-up.',
      invalid_date: 'Choose a valid date.',
      invalid_time: 'Choose a valid time.',
      invalid_amount: 'Something went wrong on our side. Please try again shortly.',
      invalid_quantity: 'Check the quantities you chose.',
      invalid_rate: 'Something went wrong on our side. Please try again shortly.',
      nothing_to_pay: 'There is nothing to pay on this booking.',
      booking_not_payable: 'This booking cannot be paid online.',
      booking_disputed: 'This booking is settled at our counter, in cash or by card.',
      payment_processing:
        'A payment for this booking is being confirmed. Please wait a few minutes before trying again.',
      online_payment_pending: 'Online payment is not available yet: you pay at pick-up.',
      hold_ended:
        'The time to pay online has run out and the vehicle is no longer held: you can book again.',
      contact_unavailable: 'The form cannot send messages right now: call us or write on WhatsApp.',
      contact_check_failed:
        'The anti-spam check did not pass: try again, or call us or write on WhatsApp.',
      payment_provider_error:
        'The payment service is not answering. Nothing was charged: please try again shortly.',
      signing_required:
        'This screen is no longer active. Please hand the device back to the staff.',
      contract_changed:
        'The contract changed in the meantime. Please hand the device back to the staff, who will show it to you again.',
      signature_invalid: 'The signature cannot be read. Clear it and sign again.',
    },
  },
  dataExport: {
    fileName: 'README.txt',
    title: 'Your data at {company}',
    intro:
      'This archive holds the personal data {company} keeps about you, prepared on {date} for your request of {requestedOn} ({purpose}).',
    purposes: {
      access: 'access to your data, art. 15 GDPR',
      portability: 'data portability, art. 20 GDPR',
    },
    contentsTitle: 'What it holds',
    contents: [
      'data.json: your data, in a form a program can read (JSON, UTF-8).',
      'files/contracts: the contracts you signed, as PDFs.',
      'files/fine-letters: the letters naming the driver to the authority, when a fine came for one of your rentals.',
      'files/documents: the photos of your documents, if you asked for them and we still keep them.',
    ],
    readingTitle: 'How to read it',
    reading: [
      'In data.json: customer (your details), licences, bookings (each with its rental, contracts, payments, refunds, deposits, fiscal documents, payment pages, fines and deliveries: where we met you, a note, the ferry you arrived on), identities (as recorded at the hand-over), secondDriver (each time you were named second driver on someone else’s booking: its dates, the fee, the outcome of the requirements check), cargosReports (the reports to the Italian State Police, CaRGOS), documents (the documents uploaded), legacyRecords (from our previous system).',
      'Amounts are in euro cents: 12345 means €123.45.',
      'Dates and times follow ISO 8601; times are in UTC (Italian time is one hour ahead in winter, two in summer).',
      'A file listed with "path": null is not in the archive, and "state" says why: erased, not_requested, never_uploaded, or unreadable (it could not be read).',
    ],
    leftOutTitle: 'What it leaves out',
    leftOut: [
      'The strokes of your signature (the pen’s timing and pressure), which would let anyone imitate it: the contract PDF shows the signature as you made it.',
      'The photos and notes on the vehicle taken at the hand-over and the return: they are about the vehicle. Damages charged to you are listed with their charges.',
      'Staff names and the company’s internal records.',
      'What Stripe keeps about card payments: you can ask Stripe for it.',
    ],
    contact: 'For any question about this data, contact {company}.',
  },
}
